import React, { useState, useEffect, memo } from 'react';
import { Card } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button, RamblaActionButtonContent } from 'components';
import MarkdownText from 'components/MarkdownText/MarkdownText';
import ConditionalSpinner from 'components/ConditionalSpinner/ConditionalSpinner';
import { media } from 'utils/rwd';
import { toastr } from 'utils';

const LookCard = ({ look, onDelete, isDeleting, isActive }) => {
  const { t } = useTranslation('pages');
  const [mediaIndex, setMediaIndex] = useState(0);
  const mediaCount = look.media?.length ?? 0;
  const isVideo = look.video_ids?.length > 0;
  const canPreview = !!look.public_url;

  useEffect(() => {
    if (!isActive) setMediaIndex(0);
  }, [isActive]);

  const handlePreview = () => {
    if (look.public_url) {
      window.open(look.public_url, '_blank')?.focus();
    }
  };

  return (
    <LookCardWrapper $isActive={isActive}>
      <MediaWrapper>
        {isVideo ? (
          <video
            key={mediaIndex}
            src={look.media?.[mediaIndex]}
            poster={look.media_thumbnails?.[mediaIndex]}
            muted
            playsInline
          />
        ) : (
          <img src={look.media?.[mediaIndex]} alt={look.name} />
        )}

        {mediaCount > 1 && (
          <MediaControls>
            <MediaNavButton disabled={mediaIndex === 0} onClick={() => setMediaIndex((i) => i - 1)}>
              <FaChevronLeft />
            </MediaNavButton>
            <MediaThumbnails>
              {look.media.map((url, i) => (
                <MediaThumbnail
                  key={url}
                  $active={i === mediaIndex}
                  onClick={() => setMediaIndex(i)}
                >
                  <img src={look.media_thumbnails?.[i] ?? url} alt="" />
                </MediaThumbnail>
              ))}
            </MediaThumbnails>
            <MediaNavButton
              disabled={mediaIndex === mediaCount - 1}
              onClick={() => setMediaIndex((i) => i + 1)}
            >
              <FaChevronRight />
            </MediaNavButton>
          </MediaControls>
        )}
        {look.visibility && <VisibilityBadgeLabel>{look.visibility}</VisibilityBadgeLabel>}
      </MediaWrapper>

      <Header>
        <LookName>{look.name}</LookName>
      </Header>

      <Body>
        {look.description && <Description>{look.description}</Description>}

        {look.products?.length > 0 && (
          <>
            <ProductsLabel>{t('retailerPage.looks.products')}</ProductsLabel>
            <ProductsRow>
              {look.products.map((product) => {
                const cp = product.child_products?.[0]?.child_product;
                if (!cp) return null;
                return (
                  <ProductItem key={product.uid}>
                    <ProductImageWrapper>
                      {cp.picture && (
                        <ProductImage src={cp.picture} alt={cp.parent_product?.name} />
                      )}
                    </ProductImageWrapper>
                    <ProductFooterInfo>
                      <ProductName>{cp.parent_product?.name}</ProductName>
                      {cp.product_code && <ProductMeta>{cp.product_code}</ProductMeta>}
                    </ProductFooterInfo>
                  </ProductItem>
                );
              })}
            </ProductsRow>
          </>
        )}
      </Body>

      <Footer>
        <Button
          variant="primary"
          size="small"
          onClick={() => {
            toastr.confirm(t('toastr:deleteRetailerLookHeading'), {
              onOk: () => {
                onDelete(look.uid);
              },
              description: t('toastr:deleteRetailerLookDescription'),
              cancelText: t('toastr:cancel'),
              okText: t('toastr:delete'),
            });
          }}
          disabled={isDeleting}
        >
          <ConditionalSpinner showSpinner={isDeleting}>
            {t('retailerPage.looks.delete')}
          </ConditionalSpinner>
        </Button>
        {canPreview && (
          <Button variant="avedaPrimary" size="small" onClick={handlePreview}>
            <RamblaActionButtonContent icon="preview">
              {t('retailerPage.looks.preview')}
            </RamblaActionButtonContent>
          </Button>
        )}
      </Footer>
    </LookCardWrapper>
  );
};

export default memo(LookCard);

const LookCardWrapper = styled(Card)`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto 1fr auto;
  grid-template-areas:
    'header'
    'img'
    'content'
    'footer';
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e9ecef;
  box-shadow: ${({ $isActive }) =>
    $isActive ? '0 8px 15px rgba(0, 0, 0, 0.1)' : '0 5px 10px rgba(0, 0, 0, 0.05)'};

  ${media.xs`
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto 1fr auto;
    grid-template-areas:
    'img header'
    'img content'
    'img footer';
  `}
`;

const MediaWrapper = styled.div`
  grid-area: img;
  position: relative;
  overflow: hidden;
  img,
  video {
    width: 100%;
    height: 200px;
    aspect-ratio: 1 / 1;
    object-fit: contain;
    ${media.xs`
      aspect-ratio: auto;
      object-fit: cover;
      height: 100%;
    `}
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-area: header;
  padding: 16px 24px;
  border-bottom: 2px solid #e9ecef;
  ${media.xs.down`
    padding: 10px 16px;
  `}
`;

const VisibilityBadgeLabel = styled.span`
  position: absolute;
  top: 16px;
  left: 16px;
  align-self: flex-start;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: bold;
  color: #1a2b33;
  background-color: #fff;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid #e9ecef;
  ${media.xs`
    font-size: 12px;
  `}
`;

const LookName = styled.h3`
  margin: 0;
  font-size: 28px;
  font-weight: bold;
  ${media.xs.down`
    font-size: 20px;
  `}
`;

const Body = styled.div`
  grid-area: content;
  padding: 16px 24px;
  overflow-y: auto;
  min-height: 200px;
  scrollbar-color: #e9ecef transparent;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #e9ecef;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #6c757d;
  }
  ${media.xs.down`
    padding: 10px 16px;
  `}
`;

const Description = styled(MarkdownText)`
  margin-bottom: 12px;
  font-size: 16px;
  color: #6c757d;
`;

const ProductsLabel = styled.p`
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: bold;
  color: #1a2b33;
  text-transform: uppercase;
  ${media.xs.down`
    font-size: 14px;
  `}
`;

const ProductsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  ${media.xs.down`
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  `}
  ${media.xs`
    grid-template-columns: 1fr;
  `}
  ${media.lg`
    grid-template-columns: repeat(2, 1fr);
  `}
  ${media.xl`
    grid-template-columns: repeat(3, 1fr);
  `}
`;

const ProductItem = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0 1px 3px #e9ecef30;
  border: 1px solid #e9ecef;
  overflow: hidden;
  ${media.xs.down`
    flex-shrink: 0;
    width: 120px;
  `}
`;

const ProductImageWrapper = styled.div`
  border-bottom: 1px solid #e9ecef;
`;

const ProductImage = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
`;

const ProductFooterInfo = styled.div`
  display: flex;
  padding: 8px 12px;
  flex-direction: column;
  gap: 4px;
`;

const ProductName = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #1a2b33;
  ${media.xs.down`
    font-size: 14px;
  `}
`;

const ProductMeta = styled.span`
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
`;

const Footer = styled.div`
  grid-area: footer;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 0.75rem 1.5rem;
  border-top: 2px solid #e9ecef;
`;

const MediaControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.4));
`;

const MediaNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.25);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  flex-shrink: 0;
  transition: background 0.2s ease;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.5);
  }
  &:disabled {
    opacity: 0;
    pointer-events: none;
  }
`;

const MediaThumbnails = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MediaThumbnail = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 2px solid ${({ $active }) => ($active ? 'white' : 'transparent')};
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  opacity: ${({ $active }) => ($active ? 1 : 0.55)};
  transition:
    opacity 0.2s ease,
    border-color 0.2s ease;
  &:hover {
    opacity: 1;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;
